import React, { useMemo, useState } from 'react';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import useGetBatchAssortmentSwapRecommendations from '@store/hooks/useGetBatchAssortmentSwapRecommendations';
import {
  AgileBatchRecommedationInput,
  AgileRecommendationInput,
  AgileRecommendationResult,
  AgileRecommendationTypes,
} from '@components/agile/types/AgileRecommendationTypes';
import AtRecommendationTable from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneComponents/AtAcrossZoneRecommendationTableComponent/AtRecommendationTable';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { AssortmentFacingUpdate, ErrorType, SwapAssortmentInput } from 'src/graphql/generated/graphql';
import useBatchSwapAssortmentAcrossZone from 'src/store/hooks/useBatchSwapAssortmentAcrossZone';
import usePromoteDraftZone from 'src/store/hooks/usePromoteDraftZone';
import AgileUploadRow from 'src/components/agile/types/AgileUploadRow';
import { mergeDuplicateProductChanges } from 'src/components/agile/utils/RecommendationTableUtils';
import SwapAssortmentConfirmationModalComponent from './AtAcrossZoneRecommendationTableComponent/SwapAssortmentConfirmationModalComponent';

export interface AtBulkUploadDiffTableProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  recommendationInput: AgileBatchRecommedationInput[];
  swapTime: number;
  diffTableData: AgileUploadRow[];
  macroplanogramToFacingUnitsMap: Record<string, number>;
  setInputTableFlag: (value: boolean) => void;
  setRecommendationFlag: (value: boolean) => void;
  updatedRecommendInputWithDiffData: AgileBatchRecommedationInput[];
  includeDraft: boolean;
}

const AtAcrossZoneRecommedationTable = (props: AtBulkUploadDiffTableProps) => {
  const {
    recommendationInput,
    swapTime,
    listOfMacroplanogram,
    diffTableData,
    macroplanogramToFacingUnitsMap,
    setInputTableFlag,
    setRecommendationFlag,
    updatedRecommendInputWithDiffData,
    includeDraft,
  } = props;
  const [disableButton, setDisableButton] = useState<boolean[]>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openPromoteDraftErrorModal, setOpenPromoteDraftErrorModal] = useState<{
    openStatus: boolean;
    errorMessage: string | undefined;
  }>({ openStatus: false, errorMessage: '' });
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openPromoteDraftSuccessModal, setOpenPromoteDraftSuccessModal] = useState<boolean>(false);
  const [batchSwapInput, setBatchSwapInput] = useState<SwapAssortmentInput[]>([]);
  const [filteredBatchSwapInput, setFilteredBatchSwapInput] = useState<SwapAssortmentInput[]>([]);

  const [macroplanogramDataMap, setMacroplanogramDataMap] = useState<
    Record<
      string,
      {
        filterFacingUpdatesList: AgileRecommendationTypes[];
        uniqueNewAsinList: AgileRecommendationInput[];
      }
    >
  >({});
  const [rowData, setRowData] = useState<AgileRecommendationResult[]>([]);
  const [errorMap, setErrorMap] = useState<
    {
      key: string;
      error: { errorMessage: string; errorType?: ErrorType | null };
    }[]
  >([]);
  const { mutateAsync: batchSwapAssortmentAcrossZone } = useBatchSwapAssortmentAcrossZone();
  const { isLoading, recommendations } = useGetBatchAssortmentSwapRecommendations({
    batchAssortmentSwap: recommendationInput,
  });
  const onBatchSwapAssortmentAcrossZone = async () => {
    await batchSwapAssortmentAcrossZone({
      input: {
        swapAssortmentInputList: filteredBatchSwapInput,
      },
    }).then((resp) => {
      // eslint-disable-next-line promise/always-return
      if (resp.batchSwapAssortmentAcrossZone.errorMap.length === 0) {
        setOpenSuccessModal(true);
      } else {
        console.error('error occured during batch swap', resp.batchSwapAssortmentAcrossZone.errorMap);
        setErrorMap(resp.batchSwapAssortmentAcrossZone.errorMap);
        setOpenErrorModal(true);
      }
    });
  };
  const getMinimumDraftStartTime = (macroplanograms: AgileMacroplanogram[] | undefined): number => {
    if (!macroplanograms || macroplanograms.length === 0) {
      return 0;
    }

    const draftMacroplanograms = macroplanograms.filter((macro) => macro.isDraft);
    if (draftMacroplanograms.length === 0) {
      return 0;
    }

    const startTimes = draftMacroplanograms.map((macro) => parseInt(macro.startTime, 10));
    return startTimes.length > 0 ? Math.min(...startTimes) : 0;
  };
  const { mutateAsync: promoteDraftZone } = usePromoteDraftZone();
  const onPublishDraftZone = async () => {
    try {
      setOpenConfirmationModal(false); // Close confirmation modal first
      const swapResult = await batchSwapAssortmentAcrossZone({
        input: {
          swapAssortmentInputList: filteredBatchSwapInput,
        },
      });

      if (swapResult.batchSwapAssortmentAcrossZone.errorMap.length > 0) {
        setErrorMap(swapResult.batchSwapAssortmentAcrossZone.errorMap);
        setOpenErrorModal(true);
        return;
      }

      // fetch the start time of draft zone map to promote the draft to live
      const minDraftStartTime = getMinimumDraftStartTime(listOfMacroplanogram);
      console.log('The startTime of the draft macropogs: ', minDraftStartTime);

      const promoteResult = await promoteDraftZone({
        input: {
          storeId: filteredBatchSwapInput[0]?.storeId,
          timestamp: minDraftStartTime,
        },
      });

      if (promoteResult.promoteDraftZone.error === null) {
        setOpenPromoteDraftSuccessModal(true);
      } else {
        setOpenPromoteDraftErrorModal({
          openStatus: true,
          errorMessage: promoteResult.promoteDraftZone.error?.errorMessage || 'Unknown error occurred',
        });
      }
    } catch (error) {
      setOpenPromoteDraftErrorModal({
        openStatus: true,
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  useMemo(() => {
    // filter out recommendation with zero result
    const numberOfRecommendationTable =
      Array.isArray(recommendations) && recommendations.length > 0 ? recommendations.length : 0;
    if (recommendations && recommendations.length > 0) {
      setDisableButton(new Array(numberOfRecommendationTable).fill(true));
      setRowData(recommendations);
    }
  }, [recommendations]);

  const handleDisableButton = (index: number, newDisableButtonState: boolean) => {
    setDisableButton((prevDisableButton) => {
      const updatedDisableButton = [...prevDisableButton];
      updatedDisableButton[index] = newDisableButtonState;
      return updatedDisableButton;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notEmpty = (...arrays: (any[] | undefined)[]) => arrays.some((array) => array && array.length > 0);

  const recommendationDataHandler = (index: number, recommendation: AgileRecommendationTypes[]) => {
    const updatedRowData = [...rowData];
    updatedRowData[index].results = recommendation;
    setRowData(updatedRowData);
  };
  const handleSubmitSelection = () => {
    const nonZeroFacingAssortmentsByZone = updatedRecommendInputWithDiffData.map((inputAssortment) => {
      const assortmentsWithNonZeroFacings = inputAssortment.addAssortment.filter(
        (assortment) => +assortment.facingUnits !== 0,
      );
      return {
        ...inputAssortment,
        addAssortment: assortmentsWithNonZeroFacings,
      };
    });
    const filteredSwapInput: SwapAssortmentInput[] = [];
    const AssortmentListToUpdateByZone = rowData.map((data) => ({
      macroplanogramId: data.macroplanogramId,
      assortmentFacingUpdates: data.results.map((assortment) => ({
        entryId: assortment.entryId,
        facingUnits: parseInt(String(assortment.remainingFacings), 10),
        fohCapacity: assortment.remainingCapacity,
      })),
    }));

    const SwapInput = nonZeroFacingAssortmentsByZone.map((newAssortment) => {
      const assortmentToUpdateByZone = AssortmentListToUpdateByZone.find(
        (assortmentToUpdate) => assortmentToUpdate.macroplanogramId === newAssortment.macroplanogramId,
      );
      if (assortmentToUpdateByZone) {
        return {
          ...newAssortment,
          assortmentFacingUpdates: assortmentToUpdateByZone.assortmentFacingUpdates,
        };
      }
      return { ...newAssortment, assortmentFacingUpdates: [] };
    });
    const macroDataMap: Record<
      string,
      {
        filterFacingUpdatesList: AgileRecommendationTypes[];
        uniqueNewAsinList: AgileRecommendationInput[];
      }
    > = {};
    listOfMacroplanogram?.forEach((macroplanogram) => {
      const { uniqueRecommendedAsinList, uniqueNewAsinList } = mergeDuplicateProductChanges(
        nonZeroFacingAssortmentsByZone.find((input) => input.macroplanogramId === macroplanogram.entryId)
          ?.addAssortment || [],
        rowData.filter((data) => data.macroplanogramId === macroplanogram.entryId)[0]?.results || [],
      );

      const filterFacingUpdatesList = uniqueRecommendedAsinList?.filter(
        (data) =>
          +data.difference !== +0 ||
          Math.trunc((data.fohCapacity / data.availableFacings) * data.remainingFacings - data.remainingCapacity) !== 0,
      );
      if (notEmpty(filterFacingUpdatesList, uniqueNewAsinList)) {
        filteredSwapInput.push({
          storeId: macroplanogram.storeId,
          swapTime,
          endTime: macroplanogram.endTime,
          macroplanogramId: macroplanogram.entryId,
          addAssortment: uniqueNewAsinList,
          assortmentFacingUpdates: filterFacingUpdatesList?.map(
            (assortment) =>
              ({
                entryId: assortment.entryId,
                facingUnits: parseInt(String(assortment.remainingFacings), 10),
                fohCapacity: assortment.remainingCapacity,
              }) as AssortmentFacingUpdate,
          ),
          isDraft: includeDraft,
        } as SwapAssortmentInput);
        macroDataMap[macroplanogram.entryId] = {
          filterFacingUpdatesList,
          uniqueNewAsinList,
        };
      }
    });
    setMacroplanogramDataMap(macroDataMap);
    setBatchSwapInput(SwapInput);
    setFilteredBatchSwapInput(filteredSwapInput);
    setOpenConfirmationModal(true);
  };

  return isLoading && recommendations === undefined ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <>
      <Row spacing="400" alignmentHorizontal="justify" spacingInset="400">
        <Text type="h400">Adjust existing capacity of products in the zones.</Text>
      </Row>
      {recommendations &&
        recommendations.map((recommendation, index) => {
          const zoneDetails = listOfMacroplanogram?.find(
            (macroplanogramDetails) => macroplanogramDetails.entryId === recommendation.macroplanogramId,
          );
          return (
            <AtRecommendationTable
              index={index}
              recommendation={recommendation}
              disableButtonState={handleDisableButton}
              recommendationDataHandler={recommendationDataHandler}
              macroplanogramToFacingUnitsMap={macroplanogramToFacingUnitsMap}
              zoneDetails={zoneDetails}
            />
          );
        })}
      <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
        <Button onClick={handleSubmitSelection} disabled={!disableButton.every((item) => !item)}>
          Review changes
        </Button>
      </Row>
      <SwapAssortmentConfirmationModalComponent
        openConfirmationModal={openConfirmationModal}
        setOpenConfirmationModal={setOpenConfirmationModal}
        openErrorModal={openErrorModal}
        setOpenErrorModal={setOpenErrorModal}
        openSuccessModal={openSuccessModal}
        setOpenSuccessModal={setOpenSuccessModal}
        openPromoteDraftSuccessModal={openPromoteDraftSuccessModal}
        setOpenPromoteDraftSuccessModal={setOpenPromoteDraftSuccessModal}
        batchSwapInput={batchSwapInput}
        listOfMacroplanogram={listOfMacroplanogram}
        diffTableData={diffTableData}
        onBatchSwapAssortmentAcrossZone={onBatchSwapAssortmentAcrossZone}
        errorMap={errorMap}
        macroplanogramDataMap={macroplanogramDataMap}
        setInputTableFlag={setInputTableFlag}
        setRecommendationFlag={setRecommendationFlag}
        isDraft={includeDraft}
        onPublishDraftZone={onPublishDraftZone}
        openPromoteDraftErrorModal={openPromoteDraftErrorModal}
        setOpenPromoteDraftErrorModal={setOpenPromoteDraftErrorModal}
      />
    </>
  );
};

export default AtAcrossZoneRecommedationTable;
